import BaseClient from "../baseClient";
import PayPalApprovalResponse from "./payPalApprovalResponse";
import CheckoutLoadResponse from "./checkoutLoadResponse";
import CheckoutSubmitRequest from "./checkoutSubmitRequest";
import CheckoutSubmitResponse from "./checkoutSubmitResponse";
import GetSiteGeometryResponse from "./getSiteGeometryResponse";
import OrderProcessRequest from "./orderProcessRequest";
import PayPalAuthorisationRequest from "./payPalAuthorisationRequest";
import SelectOptionsLoadResponse from "./selectOptionsLoadResponse";
import SelectOptionsSubmitRequest from "./selectOptionsSubmitRequest";
import UpdateSiteGeometryRequest from "./updateSiteGeometryRequest";
import UpdateSiteGeometryResponse from "./updateSiteGeometryResponse";
import ViewEstimateLoadResponse from "./viewEstimateLoadResponse";
import RestartDraftOrderRequest from "./restartDraftOrderRequest";
import RestartDraftOrderResponse from "./restartDraftOrderResponse";
import AddProductsLoadRequest from "./addProductsLoadRequest";
import AddProductsLoadResponse from "./addProductsLoadResponse";
import AddProductsSubmitRequest from "./addProductsSubmitRequest";
import AddProductsPriceResponse from "./addProductsPriceResponse";
import DocumentDetailsResponse from "../account/documentDetailsResponse";
import GenerateOnlineQuoteResponse from "./generateOnlineQuoteResponse";
import OrderWktRequest from "./orderWktRequest";
import OrderWktResponse from "./orderWktResponse";
import PlaceSearchRequest from "./placeSearchRequest";
import PlaceSearchResponse from "./placeSearchResponse";

/**
 * Client for calling actions on the OrderProcess controller.
 */
export default class OrderProcessClient extends BaseClient {

    /**
     * Complete (capture) a PayPal order.
     */
    public async CompletePayWithPayPalAsync(requestContent: PayPalAuthorisationRequest): Promise<CheckoutSubmitResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_pay_paypal_complete');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'complete-pay-with-paypal',
            method: 'post',
            body: requestContent
        });
        return response as CheckoutSubmitResponse;
    }

    public async GetPlaceSearchResultsAsync(requestContent: PlaceSearchRequest): Promise<PlaceSearchResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_area_place_search');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'get-place-search-results',
            method: 'post',
            body: requestContent
        });
        return response as PlaceSearchResponse;
    }

    /**
     * Generate an online quote.
     */
     public async GenerateOnlineQuoteAsync(requestContent: OrderProcessRequest): Promise<GenerateOnlineQuoteResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('generate_online_quote');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'generate-online-quote',
            method: 'post',
            body: requestContent
        });
        return response as GenerateOnlineQuoteResponse;
    }


    /**
     * Gets the site geometry for an order.
     */
    public async GetSiteGeometryAsync(requestContent: OrderProcessRequest): Promise<GetSiteGeometryResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('get_site_geometry');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'get-site-geometry',
            method: 'post',
            body: requestContent
        });
        return response as GetSiteGeometryResponse;
    }

    /**
     * Loads the checkout page details.
     */
     public async LoadCheckoutDetailsAsync(requestContent: OrderProcessRequest): Promise<CheckoutLoadResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_checkout_load');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'checkout-load',
            method: 'post',
            body: requestContent
        });
        return response as CheckoutLoadResponse;
    }

    /**
     * Handle the submission of the checkout page when the customer is placing the order 'on account'.
     */
     public async PayOnAccountAsync(requestContent: CheckoutSubmitRequest): Promise<CheckoutSubmitResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_pay_on_account');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'pay-on-account',
            method: 'post',
            body: requestContent
        });
        return response as CheckoutSubmitResponse;
    }

    /**
     * Handle the submission of the checkout page when the customer is requesting to be placed on account.
     */
     public async RegisterForInvoicingAsync(requestContent: CheckoutSubmitRequest): Promise<CheckoutSubmitResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_register_for_invoicing');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'register-for-invoicing',
            method: 'post',
            body: requestContent
        });
        return response as CheckoutSubmitResponse;
    }

    

    /**
     * Handle the submission of the checkout page when the customer is paying for the order using PayPal.
     */
     public async PayWithPayPalAsync(requestContent: CheckoutSubmitRequest): Promise<PayPalApprovalResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_pay_paypal');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'pay-paypal',
            method: 'post',
            body: requestContent
        });
        return response as PayPalApprovalResponse;
    }

    /**
     * Marks the order as saved as draft without making any other changes.
     */
     public async SaveAsDraftAsync(requestContent: OrderProcessRequest): Promise<void> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('save_draft');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'save-draft',
            method: 'post',
            body: requestContent
        });
    }


    /**
     * Gets the order enquiry details.
     */
     public async SelectOptionsLoadAsync(requestContent: OrderProcessRequest): Promise<SelectOptionsLoadResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_select_options_load');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'select-options-load',
            method: 'post',
            body: requestContent
        });
        return response as SelectOptionsLoadResponse;
    }

    /**
     * Submits order details from the select options page.
     */
     public async SubmitSelectOptionsDetailsAsync(requestContent: SelectOptionsSubmitRequest): Promise<void> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_select_options_submit');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'select-options-submit',
            method: 'post',
            body: requestContent
        });
    }

    public async GetOrderWktAsync(request: OrderWktRequest) : Promise<OrderWktResponse>
    {
        request.recaptchaToken = await this.recaptchaChallengeAsync("get_order_wkt");

        const response = await this.makeRequestAsync({
            controller: 'orderProcess',
            action: 'get_order_wkt',
            method: 'post',
            body: request
        });

        return response as OrderWktResponse;
    }

    /**
     * Updates the site geometry on the current in progress order, a new order is created if no order is in progress.
     */
     public async UpdateSiteGeometryAsync(requestContent: UpdateSiteGeometryRequest): Promise<UpdateSiteGeometryResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('update_site_geometry');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'update-site-geometry',
            method: 'post',
            body: requestContent
        });
        return response as UpdateSiteGeometryResponse;
    }

    /**
     * Gets the view estimate details.
     */
     public async ViewEstimateLoadAsync(requestContent: OrderProcessRequest): Promise<ViewEstimateLoadResponse> {

        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_view_estimate_load');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'view-estimate-load',
            method: 'post',
            body: requestContent
        });
        return response as ViewEstimateLoadResponse;
    }


    /**
     * Generates a order in progress token to continue ordering a draft order.
     */
    public async restartDraftOrder(requestContent: RestartDraftOrderRequest): Promise<RestartDraftOrderResponse>
    {
        requestContent.recaptchaToken = await super.recaptchaChallengeAsync('restart_draft_order');

        const response = await super.makeRequestAsync({
            controller: 'orderProcess',
            action: 'restart-draft-order',
            method: 'post',
            body: requestContent
        });

        return response as RestartDraftOrderResponse;
    }



    /**
     * Loads the initial data for the add products page
     */
     public async addProductsLoad(requestContent: AddProductsLoadRequest): Promise<AddProductsLoadResponse>
     {
         requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_add_products_load');
 
         const response = await super.makeRequestAsync({
             controller: 'orderProcess',
             action: 'add-products-load',
             method: 'post',
             body: requestContent
         });
 
         return response as AddProductsLoadResponse;
     }


     /**
     * Submits the selected options from the add products page
     */
      public async addProductsSubmit(requestContent: AddProductsSubmitRequest): Promise<void>
      {
          requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_add_products_submit');
  
          await super.makeRequestAsync({
              controller: 'orderProcess',
              action: 'add-products-submit',
              method: 'post',
              body: requestContent
          });
      }

    /**
     * Submits the selected options from the add products page
     */
       public async addProductsCalculatePrices(requestContent: AddProductsSubmitRequest): Promise<AddProductsPriceResponse>
       {
           requestContent.recaptchaToken = await super.recaptchaChallengeAsync('order_add_products_calculate_price');
   
           const response = await super.makeRequestAsync({
               controller: 'orderProcess',
               action: 'add-products-calculate-prices',
               method: 'post',
               body: requestContent
           });

           return response as AddProductsPriceResponse;
       }


}